import functionImage from './function.png'
import functionSideEffects from './function-side-effects.png'
import manyFunctionGlobalVariables from './many-functions-global-variables.png'

const content = `<p>One of the first things that we learn as professional programmers is to avoid global variables. And indeed, global variables do damage the design of an application.</p>

<p>But could you pinpoint exactly what’s wrong with global variables?</p>

<p>The problem with global variables is that they represent <strong>uncontrolled side effects</strong>. To better understand this, let’s first define what side effects are, and then what it means to control side effects, by illustrating with the example of global variables. We’ll quickly jump to other types of side effects such as changes to the database, class attributes and many more.</p>

<p>We’ll see how all those types of side effects can damage your code by creating coupling, and how to reduce coupling by controlling the side effects.</p>

<h2 id="What-precisely-is-wrong-with-global-variables">What precisely is wrong with global variables</h2>

<p>When you consider the basic definition of a function, it is an operation that takes inputs and produces outputs:</p>

<img src='${functionImage}' alt="function" />

<p>These inputs and outputs are the <strong>contract</strong> of the function. This corresponds in code to a function taking parameters and producing return values:</p>

<pre><code class="language-ruby">def my_function(input1, input2, input3)
  ...
  &#91;output1, output2]
end</code></pre>

<p>This is also called a pure function, or a function with no side effects. For a fixed given set of inputs, this function produces the same outputs every time.</p>

<h3 id="Side-effects">Side effects</h3>

<p>Side effects are the inputs and outputs of the function that are not the parameters or return values of the function. They are inputs and outputs on the side, that are not declared in the contract of the function:</p>

<img src='${functionSideEffects}' alt="function-side-effects" />

<p>Some definitions of side effects include inputs side effects, and some don’t include them. But since input side effects are just as damaging as output side effects, we’ll keep them in our definition here.</p>

<p>Global variables, for one thing, are a type of side effects:</p>

<pre><code class="language-ruby">def my_function(input1, input2, input3)
  ...
  if (global_variable1 > 50) // inputs side effect
    output1 = 100
    global_variable2 = 25 // output side effect
  end
  
  &#91;output1, output2]
end</code></pre>

<p>This code has an input side effect on line 3 because it reads from a global variable, which is not part of the contract of the function (its parameters).</p>

<p>It also has an output side effect on line 5, because one of the effects of the function is to modify a global variable, which is not in the contract of the function (its returned values).</p>

<p>So reading and modifying global variables are side effects.</p>

<h3 id="Uncontrolled-side-effects">Uncontrolled side effects</h3>

<p>The problem with global variables is that they can be accessed from any function in the program (they’re global) and they can be modified by any function in the program (they’re variable).</p>

<p>This creates a massive coupling between all the functions together, as each function can modify the global variables that the others can read. Put differently, a change to any function could impact all the other functions via the global variables.</p>

<p>This makes the code difficult to change and reason about. Here is a representation with 4 functions and 2 global variables:</p>

<img src='${manyFunctionGlobalVariables}' alt="many-function-global-variables" />

<p>Each function has to know about whether and what the others are doing with the global variables. This creates coupling between each function and the others.</p>

<p>Those side effects are <strong>uncontrolled</strong> because they can spread very far, potentially to every function in the program. This creates massive coupling.</p>

<p>Even if a function doesn’t have any side effects, it could have indirect side effects by calling a function that calls a function that has side effects. And if doesn’t for now, maybe it will in the future.</p>

<p>In summary, global variables introduce uncontrolled side effects.</p>

<p>How to fix this? There are two possible ways: removing the side effects (by only using the contract of the functions) or controlling the side effects.</p>

<h2 id="How-to-fix-uncontrolled-side-effects">How to fix uncontrolled side effects</h2>

<p>Completely removing side effects from your program may not always be easy, nor always possible. The idea of <strong>controlling side effects</strong> is rather to limit the reach of the side effects, or put differently, to allow side effects in only a small part of the code.</p>

<p>To better understand this, let’s review common types of side effects and see how we can control (or remove) each of them.</p>

<p>In decreasing order of reach (and therefore, coupling) we will see:</p>

<ul><li>environment variables</li><li>global variables</li><li>singletons</li><li>the DB and other external storage</li><li>the IO</li><li>React contexts</li><li>exceptions used as control flow</li><li>class and instance attributes</li><li>modifying function inputs</li><li>and more.</li></ul>

<h3 id="Environment-variables">Environment variables</h3>

<p>Environment variables are global variables that are set from the outside of the program. One way to control them and limit their reach is to read them only at very specific places in the codebase and pass them around like normal function parameters afterwards.</p>

<h3 id="Global-variables">Global variables</h3>

<p>Do what you can to avoid global variables.</p>

<p>If you have existing code that uses global variables, a first step to improve the code is to limit their scope. For example, try to limit their usage to one file, and remove from that file the code that doesn’t use the global variable.</p>

<p>If that’s possible, remove the global variable and pass it around as function parameters. But that can be less than convenient if there is a lot of code that uses it.</p>

<p>Another option to mitigate its exposure is to turn the contents of that file to a class, where the global variables becomes a private class variable. For example, code like this:</p>

<pre><code class="language-ruby">my_global_variable = 42

def my_function
  my_global_variable += 1
end

def my_other_function
  my_global_variable -= 1
end</code></pre>

<p>Would become this:</p>

<pre><code class="language-ruby">class MyClass
  def self.my_function
    my_variable += 1
  end

  def self.my_other_function
    my_variable -= 1
  end
  
private
  attr_accessor :my_variable
end</code></pre>

<p>And if you can’t limit their usage to one file, still try to pass them around as function parameters instead of accessing them from within functions.</p>

<h3 id="Singletons">Singletons</h3>

<p>Singletons are generally used as eeglobal variables (that cannot be copied). Treat them like global variables.</p>

<h3 id="External-storage">External storage</h3>

<p>Accessing a database, Redis, S3 or any other external system is a side effect. They are necessary side effects, but they can be uncontrolled in the backend of an application.</p>

<p>One way to control them is to define the parts of the code that are allowed to access the external storage, and those that are not allowed.</p>

<p>For example, in a model-view-controller architecture, views should not access the database. This may be difficult to enforce, as nothing may prevent a view from calling a function that calls a function that ends up accessing the database. But paying attention to this already gets a long way.</p>

<p>It is common to define the part of the code that accesses the external storage as “repository”, and the repository presents a set of functions to provide data to the rest of the program. The rest of the code has to use this set of functions to access the data. There can be several repositories in a program, that access different kinds of data.</p>

<h3 id="IO">IO</h3>

<p>Just like external storage, accessing other kinds of IO (standard input and output, local files, external services, the printer, etc.) are side effects, that can become uncontrolled if used without restriction.</p>

<p>And like external storage, one way to limit them is to allow access to them in tiny parts of the code, and then pass the data around as function parameters and return values.</p>

<h3 id="React-Contexts">React Contexts</h3>

<p>The React frontend library offers the feature of contexts, which are nodes in the component tree that define variables that can be accessed and modified by any component below them in the tree.</p>

<p>From the perspective of such a component, accessing the variables provided by the contexts is a side effect. Indeed, the contract of a React component only contains its props (its inputs) and the tree to render (its output).</p>

<p>To control this kind of side effect, push down contexts as far down in the component tree as you can, so as to reduce the number of components that can reach out to the context. Also, give some consideration to the option of not using a context and passing parameters down the call stack when it’s not too cumbersome.</p>

<h3 id="Exceptions-used-as-control-flow">Exceptions used as control flow</h3>

<p>A programming technique (but that is considered very bad practice) is to use the exception mechanism to return results from a function:</p>

<pre><code class="language-ruby">function myFunction (input1, input2) {
  if (input1 &lt; 12) {
    throw input2 * 5 // BAD! DON'T DO THAT!
  }
  return 42
}</code></pre>

<p>And somewhere up the stack:</p>

<pre><code class="language-ruby">try {
  someFunction() // this ends up calling myFunction(9, 10)
} catch (result) {
  console.log(result) // prints 50
}</code></pre>

<p>In this code <code>my_function</code> returns its result by using <code>throw</code>, in order to avoid passing its result all the way up the stack to the place it’s needed, in order to avoid stack drilling.</p>

<p>This introduces considerable coupling, because it can impact all of the indirect callers of the function.</p>

<p>To fix this, you can use the return values of the functions to produce their outputs, and keep exceptions for exceptional situations.</p>

<h3 id="Class-and-instance-attributes">Class and instance attributes</h3>

<p>From the perspective of a class method, reading and modifying the class and instance attribute are side effects, as those attributes are not parts of the contract of the method (even for private attributes):</p>

<pre><code class="language-ruby">class MyClass
  def my_method(input1, input2, input3)
    ...
    if (class_member1 > 50) // inputs side effect
      output1 = 100
      class_member2 = 25 // output side effect
    end
  
    &#91;output1, output2]
  end
  
  private
  attr_accessor :class_member1, :class_member2
end</code></pre>

<p>Those count as side effects as <code>class_member1</code> and <code>class_member2</code> are not part of the contract of <code>my_method</code>.</p>

<p>This coupling introduced by this side effect is not as bad as the previous ones, because it remains within the class.</p>

<p>However, if the class is big that can represent a wide scope. One way to fix this is to extract from the class the methods that operate on one or a on group of attributes, and create a new class, smaller, where the side effect has therefore a more limited reach.</p>

<p>Another option is to make the public methods pass the attributes to the private methods as parameters and retrieve their new values in the returned value:</p>

<pre><code class="language-ruby">class MyClass
  def my_method(input1, input2, input3)
    ...
    output2, class_member2 = my_private_method(class_member1, class_member2)
    &#91;output1, output2]
  end
  
  private
  attr_accessor :class_member1, :class_member2
  
  def my_private_method(inputA, inputB)
    if (inputA > 50)
      outputA = 100
      outputB = 25
    end
    &#91;outputA, outputB]
  end
end</code></pre>

<p>This constrains the side effect to public methods, and removes all side effects from private methods.</p>

<h3 id="Modifying-function-inputs">Modifying function inputs</h3>

<p>Another type of side effects are functions that modify their parameters:</p>

<pre><code class="language-ruby">def my_function(input1, input2, input3)
  ...
  input3 = 2 * input2 // side effect on input3  
  &#91;output1, output2]
end</code></pre>

<p>Line 3 in the above code modifies one of the input parameters. In the contract of the function <code>input3</code> is just an input, but with this code it becomes also an output, in a non declared way.</p>

<p>This creates coupling between <code>my_function</code> and the callers of <code>my_function</code>, because now the callers of <code>my_function</code> need to know something about the implementation of <code>my_function</code>. And <code>my_function</code> cannot plan for all of its callers to work correctly, because some of them may not exist yet.</p>

<p>A way to fix this is to replace this side effect by a regular return type and let the caller decide to replace the value of <code>input3</code> if it makes sense in its context:</p>

<pre><code class="language-ruby">def my_function(input1, input2, input3)
  ...
  output3 = 2 * input2
  &#91;output1, output2, output3]
end</code></pre>

<p>A common example of this pattern is a function that takes an array or a string, and appends its outputs as new elements to it instead of returning them to the caller and letting it decide what to do with them:</p>

<pre><code class="language-ruby">def my_function(input1, input2, array)
  ...
  array &lt;&lt; output1
  array &lt;&lt; output2
end</code></pre>

<h3 id="And-there-is-more">And there is more</h3>

<p>Those are just examples of common side effects, but there are more: accessing time and date, local or timezone, generating random numbers, calling external services… Everything that doesn’t come from the inputs or doesn’t affect the returned values are side effects.</p>

<p>There are many types of side effects, and they create coupling. Some create more coupling than others, and often you can reduce coupling by removing side effects or replacing them with more controlled ones.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>the contract of a function includes its parameters and its return values</li><li>side effects are the inputs and outputs of a function that are not part of its contract</li><li>side effects create coupling</li><li>controlling side effects by reducing their reach reduces coupling</li><li>there are many types of side effects, some are less reach and less coupling than others</li></ul>
</div>`

export default content
